import { useCallback, useContext, useRef, useState } from "react";
import {
  GMAIL_MAX_CONCURRENCY,
  GMAIL_MAX_RESULTS,
  GOOGLE_GMAIL_SCOPE,
  GOOGLE_OAUTH_READY,
  GoogleAuthError,
  ensureGsiLoaded,
  getThreadPreview,
  mapWithConcurrency,
  requestGoogleAccessToken,
  searchThreads,
  useGmailThreadImport,
  type GmailRetryNotice,
  type GmailThreadPreview,
} from "../api";
import { useAgentsHttp } from "../api/useAgentsHttp";
import { UserContext } from "../auth";

import styles from "./GmailSearchPanel.module.css";

interface Props {
  // The project to import the picked threads into. Each thread is
  // imported directly as a first-class email_thread upload via
  // useGmailThreadImport - threads do NOT flow through the parent's
  // picked-files queue / intake pipeline the way Local + Drive files
  // do (they're structured JSON + pulled attachments, not flat
  // Files).
  projectId: string;
  // Fired after a successful import so the parent can react (e.g.
  // surface a toast). The uploads-list query is invalidated by the
  // hook itself, so the Files table refreshes without parent help.
  onImported?: (count: number) => void;
  // Optional: panel disables itself while the parent is in a
  // submit-inflight state.
  disabled?: boolean;
  // When true, skip the outer <section> + the intro eyebrow / title /
  // hint copy. The parent provides its own section frame; we render
  // the controls inline. Used by the unified Documents panel where
  // Local / Drive / Gmail share one card.
  embedded?: boolean;
}

interface PlanQueryResponse {
  query: string;
}

// Search-box + result-list for picking Gmail threads to import into a
// project as first-class email_thread files.
//
// The search box takes NATURAL LANGUAGE: the operator types an ask
// ("DNO offers from NGED since January"), we POST it to
// /gmail/plan_query which translates it into a Gmail search query, we
// show that query read-only for transparency, then run searchThreads
// with it and list previews. On Add, each picked thread is pulled +
// structured + uploaded via useGmailThreadImport.
//
// OAuth: the operator clicks Search -> the panel ensures the Google
// Identity Services library is loaded -> requests a `gmail.readonly`
// token -> uses it for both the search call and every per-thread
// fetch in the SAME session. Token lives in this component (state +
// a ref to dedup concurrent acquisition); never persisted, never
// leaves the browser to InRange's backend.
//
// On first use after a fresh deploy, the consent dialog asks the
// operator to grant `gmail.readonly`. If the OAuth client ID's
// JavaScript origins allowlist doesn't include the current page's
// origin, the request fails with a "redirect_uri_mismatch" / "403
// origin not allowed" error - John needs to add the origin via the
// Google Cloud Console then. See `google-config.ts` notes.
export const GmailSearchPanel = ({
  projectId,
  onImported,
  disabled,
  embedded,
}: Props) => {
  const http = useAgentsHttp();
  const { user } = useContext(UserContext);
  const importThreads = useGmailThreadImport(projectId);

  const [token, setToken] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  // When off, the planner searches exactly the operator's terms; when on,
  // it may broaden role words using this project's known counterparties.
  const [expand, setExpand] = useState(true);
  // The Gmail query /gmail/plan_query produced from the prompt.
  // Shown read-only so the operator can see exactly what we searched.
  const [plannedQuery, setPlannedQuery] = useState<string | null>(null);
  const [previews, setPreviews] = useState<GmailThreadPreview[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // In-flight token acquisition dedup. Two near-simultaneous calls to
  // ensureToken (e.g. search + a quick second click) would otherwise
  // each open a consent popup; share the one in-flight promise so
  // there's exactly one prompt per page load.
  const tokenPromiseRef = useRef<Promise<string> | null>(null);

  const reset = useCallback(() => {
    setPreviews([]);
    setSelected(new Set());
    setStatus(null);
    setErr(null);
  }, []);

  // Acquire a Gmail access token, loading GSI lazily on first call.
  // The cached React-state token is reused for every Gmail call
  // within the page lifetime (`if (token) return token`). When the
  // token eventually expires (~1h), the call sites below catch 401,
  // call forceFreshToken via the retry wrapper to drop the cached
  // value, and retry once. So in steady state there is exactly ONE
  // consent prompt per page load.
  const ensureToken = useCallback(async (): Promise<string> => {
    if (token) return token;
    if (tokenPromiseRef.current) return tokenPromiseRef.current;
    const p = (async () => {
      await ensureGsiLoaded();
      const fresh = await requestGoogleAccessToken([GOOGLE_GMAIL_SCOPE]);
      setToken(fresh);
      return fresh;
    })();
    tokenPromiseRef.current = p;
    try {
      return await p;
    } finally {
      tokenPromiseRef.current = null;
    }
  }, [token]);

  // Run a Gmail call; on 401, clear the cached token, re-acquire,
  // and retry once. Wraps the call sites (search + preview) uniformly.
  // The underlying helpers throw with an "(401):" prefix in the
  // message string per `gmail.ts` - string-matching it is simpler
  // than refactoring the helpers to expose typed errors.
  const callWith401Retry = useCallback(
    async <T,>(op: (accessToken: string) => Promise<T>): Promise<T> => {
      const accessToken = await ensureToken();
      try {
        return await op(accessToken);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        // Match the literal "(401):" prefix our gmail.ts helpers
        // emit (`Gmail search failed (401): ...`) - not a bare
        // \b401\b which would false-positive on a 5xx body whose
        // JSON happens to mention "401" in prose.
        if (!/\(401\)/.test(msg)) throw e;
        // Token expired (or revoked). Drop, re-acquire, retry once.
        setToken(null);
        tokenPromiseRef.current = null;
        await ensureGsiLoaded();
        const refreshed = await requestGoogleAccessToken([GOOGLE_GMAIL_SCOPE]);
        setToken(refreshed);
        return op(refreshed);
      }
    },
    [ensureToken],
  );

  // Surfaced when a Gmail call backs off on a transient limit (429 rate /
  // concurrency, or 5xx) so the operator sees "retrying" instead of a freeze
  // or hard failure. The underlying gmail.ts helpers retry automatically; this
  // only drives the status copy.
  const notifyRetry = useCallback((n: GmailRetryNotice) => {
    setStatus(
      `Gmail is busy (HTTP ${n.status}) - retrying in ${Math.ceil(n.delayMs / 1000)}s ` +
        `(attempt ${n.attempt} of ${n.maxAttempts})…`,
    );
  }, []);

  const onSearch = useCallback(async () => {
    const p = prompt.trim();
    if (!p) return;
    reset();
    setPlannedQuery(null);
    setBusy(true);
    try {
      // Natural-language -> Gmail query via the backend planner.
      setStatus("Planning a Gmail search from your request…");
      const planned = await http<PlanQueryResponse>("/gmail/plan_query", {
        prompt: p,
        project_id: projectId,
        expand,
      });
      const q = planned.query.trim();
      setPlannedQuery(q);
      if (!q) {
        setStatus("Couldn't turn that into a Gmail search. Try rephrasing.");
        return;
      }
      setStatus("Loading Google sign-in…");
      setStatus(`Searching Gmail for ${JSON.stringify(q)}…`);
      const stubs = await callWith401Retry((tok) =>
        searchThreads(q, tok, notifyRetry),
      );
      if (stubs.length === 0) {
        setStatus("No threads matched.");
        return;
      }
      setStatus(
        `Found ${stubs.length} thread${stubs.length === 1 ? "" : "s"}; loading previews…`,
      );
      // Per-thread preview calls, fanned out at a bounded concurrency.
      // Gmail's per-mailbox concurrent-request ceiling is 50 in-flight,
      // SHARED with our backend sync on the same account - a naive
      // Promise.all over all 25 results (plus any backend in-flight, or a
      // 401-retry re-firing the batch) trips "Too many concurrent requests".
      const previewResults = await callWith401Retry((tok) =>
        mapWithConcurrency(stubs, GMAIL_MAX_CONCURRENCY, (s) =>
          getThreadPreview(s.id, tok, notifyRetry),
        ),
      );
      setPreviews(previewResults);
      setStatus(
        `Showing ${previewResults.length} of up to ${GMAIL_MAX_RESULTS}. Tick + click Add to import.`,
      );
    } catch (e) {
      // User cancelled the consent popup - not an error, just a
      // silent abort. Clear status without red box.
      if (e instanceof GoogleAuthError && e.isUserCancelled) {
        setStatus(null);
      } else {
        setErr(e instanceof Error ? e.message : String(e));
        setStatus(null);
      }
    } finally {
      setBusy(false);
    }
  }, [prompt, reset, http, callWith401Retry, projectId, expand, notifyRetry]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runImport = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      if (!user?.id) {
        setErr("Can't import - your user session isn't resolved yet.");
        return;
      }
      setBusy(true);
      setErr(null);
      setStatus(
        `Importing ${ids.length} thread${ids.length === 1 ? "" : "s"} into this project…`,
      );
      try {
        // Acquire the Gmail token up front (with 401 retry) so the
        // import mutation - which makes its own raw Gmail fetches - gets
        // a live token. The mutation handles per-thread fetch/upload;
        // a 401 mid-import is rare (token just minted) so we don't wrap
        // the mutation itself in the retry helper.
        const accessToken = await callWith401Retry(async (tok) => tok);
        const name =
          [user.given_name, user.family_name].filter(Boolean).join(" ") ||
          user.email ||
          "Unknown";
        const result = await importThreads.mutateAsync({
          project_id: projectId,
          thread_ids: ids,
          accessToken,
          pulled_by: user.id,
          pulled_by_name: name,
          onRetry: notifyRetry,
        });
        if (result.errors.length > 0) {
          const summary = result.errors
            .map((er) => `  - ${er.thread_id}: ${er.message}`)
            .join("\n");
          setErr(
            `Imported ${result.imported.length} of ${ids.length}. Failures:\n${summary}`,
          );
          setStatus(null);
        } else {
          setStatus(
            `Imported ${result.imported.length} thread${result.imported.length === 1 ? "" : "s"} into this project.`,
          );
        }
        onImported?.(result.imported.length);
        // Drop the imported ids from the selection but keep the result
        // list visible so the operator can pick more from the same
        // search.
        setSelected((prev) => {
          const next = new Set(prev);
          for (const id of result.imported) next.delete(id);
          return next;
        });
      } catch (e) {
        if (e instanceof GoogleAuthError && e.isUserCancelled) {
          setStatus(null);
        } else {
          setErr(e instanceof Error ? e.message : String(e));
          setStatus(null);
        }
      } finally {
        setBusy(false);
      }
    },
    [user, callWith401Retry, importThreads, projectId, onImported, notifyRetry],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void onSearch();
    }
  };

  if (!GOOGLE_OAUTH_READY) {
    const notReady = (
      <>
        {!embedded && <div className={styles.eyebrow}>Search Gmail</div>}
        <p className={styles.note}>
          Gmail integration is not configured for this deploy. Set{" "}
          <code>GOOGLE_CLIENT_ID</code> + <code>GOOGLE_API_KEY</code> in{" "}
          <code>web/src/api/google-config.ts</code> and add the v2 CloudFront
          origin to the Google Cloud Console allowlist.
        </p>
      </>
    );
    return embedded ? (
      <div className={styles.embedded}>{notReady}</div>
    ) : (
      <section className={styles.section}>{notReady}</section>
    );
  }

  const inflight = busy || disabled;

  const body = (
    <>
      {!embedded && (
        <div className={styles.head}>
          <div className={styles.eyebrow}>Search Gmail</div>
          <div className={styles.title}>
            Find email threads related to this project
          </div>
          <div className={styles.note}>
            Describe what you're after in plain English - e.g.{" "}
            <code>DNO offers from NGED since January</code> or{" "}
            <code>the G99 thread for Avonmouth</code>. We translate it into a
            Gmail search. Up to {GMAIL_MAX_RESULTS} threads per search.
            Read-only - the agent only sees the threads you tick + import.
          </div>
        </div>
      )}

      <div className={styles.searchRow}>
        <input
          className={styles.searchInput}
          placeholder="Describe the emails you're looking for…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={inflight}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => void onSearch()}
          disabled={inflight || !prompt.trim()}
          className={styles.searchBtn}
        >
          {busy ? "Working…" : token ? "Search" : "Sign in & search"}
        </button>
      </div>

      <label
        className={styles.note}
        style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}
        title="When on, role words like 'the DNO' are broadened using this project's known contacts. When off, only the terms you typed are searched."
      >
        <input
          type="checkbox"
          checked={expand}
          onChange={(e) => setExpand(e.target.checked)}
          disabled={inflight}
        />
        Expand my terms using this project&apos;s known contacts (off = search
        exactly what I type; put a term in quotes to match it exactly)
      </label>

      {plannedQuery && (
        <div className={styles.status}>
          Gmail search: <code>{plannedQuery}</code>
        </div>
      )}

      {err && <div className={styles.err}>{err}</div>}
      {status && !err && <div className={styles.status}>{status}</div>}

      {previews.length > 0 && (
        <>
          <ul className={styles.list}>
            {previews.map((r) => (
              <li key={r.id} className={styles.item}>
                <input
                  type="checkbox"
                  checked={selected.has(r.id)}
                  onChange={() => toggle(r.id)}
                  className={styles.checkbox}
                  aria-label={`Select thread ${r.subject}`}
                  disabled={inflight}
                />
                <div className={styles.itemBody}>
                  <div className={styles.subject}>{r.subject}</div>
                  <div className={styles.meta}>
                    {r.from && <span>{r.from}</span>}
                    {r.date && <span> · {r.date}</span>}
                    {r.messageCount > 1 && <span> · {r.messageCount} msgs</span>}
                  </div>
                  {r.snippet && <div className={styles.snippet}>{r.snippet}</div>}
                </div>
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <button
              type="button"
              onClick={() => void runImport([...selected])}
              disabled={inflight || selected.size === 0}
              className={styles.addBtn}
            >
              {busy
                ? "Working…"
                : `Import ${selected.size} thread${selected.size === 1 ? "" : "s"} to project`}
            </button>
            <button
              type="button"
              onClick={() => void runImport(previews.map((r) => r.id))}
              disabled={inflight || previews.length === 0}
              className={styles.addBtn}
            >
              {`Import all ${previews.length} thread${previews.length === 1 ? "" : "s"} to project`}
            </button>
          </div>
        </>
      )}
    </>
  );

  return embedded ? (
    <div className={styles.embedded}>{body}</div>
  ) : (
    <section className={styles.section}>{body}</section>
  );
};
